import searchAll, {
  searchLearningContent,
  searchProducts,
  searchRecipes,
} from '@api/search/searchQueries';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { captureException } from '@sentry/nextjs';
import { request } from '@utils/graphql-request';
import { swrTtlMiddleware } from '@repo/ui/src/utils/swrTtlMiddleware';
import React from 'react';
import useSWRInfinite from 'swr/infinite';

export interface FacetPair {
  value: string;
  count: number;
}

export interface Facet {
  facet: string;
  pairs: Array<FacetPair>;
}

interface SearchData {
  page: number;
  nbPages: number;
  nbHits: number;
  results: Array<any>;
  facets?: Array<Facet>;
  counts?: Record<string, number>;
}

interface Key {
  searchTerm: string;
  searchType: string;
  page: number;
  locale: string;
  productGeo?: string;
  filter?: Record<string, unknown>;
  sort?: Record<string, unknown>;
  options?: Record<string, unknown>;
  pageSize?: number;
}

interface SearchVariables {
  query: string;
  pageSize: number;
  page: number;
  filter?: Record<string, unknown>;
  sort?: Record<string, unknown>;
  options?: Record<string, unknown>;
}

interface SearchTypeConfig {
  document: string;
  rootField: string;
  hasFacets?: boolean;
  hasCounts?: boolean;
  buildVariables: (vars: SearchVariables) => Record<string, unknown>;
}

const SEARCH_TYPE_CONFIG: Record<string, SearchTypeConfig> = {
  All: {
    document: searchAll,
    rootField: 'searchGlobalTypedContentItems',
    buildVariables: ({ query, pageSize, page }) => ({ query, pageSize, page }),
  },
  Product: {
    document: searchProducts,
    rootField: 'searchProductListItems',
    hasFacets: true,
    hasCounts: true,
    buildVariables: ({ query, pageSize, page, filter, sort, options }) => ({
      query,
      pageSize,
      page,
      filter,
      sort,
      options,
    }),
  },
  Recipe: {
    document: searchRecipes,
    rootField: 'listRecipeListItems',
    hasFacets: true,
    hasCounts: true,
    buildVariables: ({ query, pageSize, page, filter, sort, options }) => ({
      query,
      pageSize,
      page,
      filter,
      sort,
      options,
    }),
  },
  Learn: {
    document: searchLearningContent,
    rootField: 'searchLearningContent',
    hasFacets: true,
    hasCounts: true,
    buildVariables: ({ query, pageSize, page, filter, sort, options }) => ({
      query,
      pageSize,
      page,
      filter,
      sort,
      options,
    }),
  },
};

const emptyResult = (page: number): SearchData => ({ page, nbPages: 0, nbHits: 0, results: [] });

export const isInGeo = (item: any, productGeo?: string) =>
  item?.productData?.productGeos
    ? item.productData.productGeos.some(({ geos }: { geos: string }) => geos === productGeo)
    : true;

export const searchFetcher = async ({
  searchTerm,
  searchType,
  page,
  locale,
  productGeo,
  filter,
  sort,
  options,
  pageSize,
}: Key): Promise<SearchData> => {
  const config = searchTerm ? SEARCH_TYPE_CONFIG[searchType] : undefined;
  if (!config) return emptyResult(page);

  const response = await request<
    Record<
      string,
      { hits: Array<any>; nbHits: number; nbPages: number; facets?: Array<Facet> } | null
    >
  >({
    document: config.document,
    variables: config.buildVariables({
      query: searchTerm,
      pageSize: pageSize ?? 30,
      page,
      filter,
      sort,
      options,
    }),
    requestHeaders: { 'Accept-Language': locale || 'en-US' },
    locale,
  });

  const { hits = [], nbHits = 0, nbPages = 0, facets } = response?.[config.rootField] ?? {};

  return {
    page,
    nbPages,
    nbHits,
    results: hits.filter((item) => isInGeo(item, productGeo)),
    ...(config.hasFacets ? { facets: facets ?? [] } : {}),
    ...(config.hasCounts && page === 0
      ? {
          counts: {
            Product: response?.CountProduct?.nbHits ?? 0,
            Recipe: response?.CountRecipe?.nbHits ?? 0,
            Learn: response?.CountLearn?.nbHits ?? 0,
          },
        }
      : {}),
  };
};

interface Props {
  searchTerm: string;
  searchType: string;
  initialSize?: number;
  filter?: Record<string, unknown>;
  sort?: Record<string, unknown>;
  options?: Record<string, unknown>;
  pageSize?: number;
}

export const useSearch = ({
  searchTerm,
  searchType,
  initialSize = 1,
  filter,
  sort,
  options,
  pageSize,
}: Props) => {
  const { locale = 'en-US', productGeo } = useCurrentUrl();
  const { data: dataArray, ...rest } = useSWRInfinite(
    (page) =>
      searchTerm && SEARCH_TYPE_CONFIG[searchType]
        ? { searchTerm, searchType, page, locale, productGeo, filter, sort, options, pageSize }
        : null,
    searchFetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
      parallel: true,
      initialSize,
      // Loading more pages must not refetch page 1
      revalidateFirstPage: false,
      use: [swrTtlMiddleware()],
      onError: (error) => captureException(error),
    },
  );
  const data = React.useMemo(() => {
    const results = dataArray?.map((item) => item.results).flat() ?? [];
    const last = dataArray?.[dataArray?.length - 1];
    return {
      page: 0,
      nbPages: 0,
      nbHits: 0,
      ...last,
      // Counts are only fetched with the first page
      counts: dataArray?.[0]?.counts,
      results,
    };
  }, [dataArray]);

  return { data, ...rest };
};
